<script setup lang="ts">
import { Enums } from "@/enums";
import { computed } from "vue";
import DiscountIcon from "~icons/mdi/discount";
import type { DiscountParam } from "../../common/types.common";
import { defineActions, defineState } from "../common";
import { FormInputCheckbox, FormInputNumeric, FormInputSelect } from "../input";
import { FormMessage, FormPanel, FormTool } from "../internal";
import { type BaseProps, defaultProps } from "../props";

type ModelType = DiscountParam;
interface PropsType extends BaseProps<ModelType> {
    toggleable?: boolean;
}

const props = withDefaults(defineProps<PropsType>(), {
    ...defaultProps<ModelType>(),
    toggleable: false,
    value: undefined,
});

const model = defineModel<ModelType>({
    required: true,
});

const state = defineState({
    model: model,
    props: props,
    hasValue: () => {
        if (model.value.isActive) {
            return model.value.cost !== undefined && model.value.unit !== undefined;
        } else {
            return true;
        }
    },
});

const actions = defineActions(model, props, state);

const prefix = computed(() => {
    if (model.value.unit === "currency") return "Rp. ";
    return undefined;
});

const suffix = computed(() => {
    if (model.value.unit === "percentage") return " %";
    return undefined;
});

const maxCost = computed(() => {
    if (model.value.unit === "percentage") return 100;
    return undefined;
});

const collapsed = computed(() => {
    if (props.toggleable) {
        return !model.value.isActive;
    } else {
        return false;
    }
});

// watch(
//     () => props.toggleable,
//     () => (model.value.isActive = props.toggleable === false),
//     { immediate: true },
// );
</script>

<template>
    <FormPanel :collapsed="collapsed" :param="props">
        <template #header>
            <span class="inline-flex flex-row items-center gap-3">
                <FormInputCheckbox
                    v-if="props.toggleable"
                    v-model="model.isActive"
                    class="ml-1"
                    :disabled="!state.editMode"
                    embedMode="inline"
                />
                <DiscountIcon v-else class="ml-1" />
                <span class="font-semibold text-gray-600 inline-flex flex-row gap-2">
                    {{ props.label ?? "Pengaturan Diskon" }}
                </span>
            </span>
        </template>

        <template #icons>
            <span class="inline-flex flex-row gap-4">
                <FormTool :actions="actions" :param="props" :showUpdate="true" :state="state" />
            </span>
        </template>

        <div v-if="state.hasError" class="p-4">
            <FormMessage :state="state" />
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormInputSelect
                v-model="model.unit"
                embedMode="embed"
                label="Satuan Diskon"
                :mode="state.nestMode"
                :options="Enums.DiscountUnitEnum.select"
            />
            <FormInputNumeric
                v-model="model.cost"
                :disabled="model.unit === undefined"
                embedMode="embed"
                :fraction="model.unit === 'percentage'"
                label="Nilai Diskon"
                :max="maxCost"
                :mode="state.nestMode"
                :prefix="prefix"
                :suffix="suffix"
            />
        </div>
    </FormPanel>
</template>
